import React from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { AppThemeProvider } from './ThemeContext';
import { AuthProvider, useAuth } from './auth/AuthContext';
import SignIn from './auth/SignIn';
import Dashboard from './pages/Dashboard';
import Onboard from './pages/Onboard';
import PropertyDetail from './pages/PropertyDetail';
import PropertyHistory from './pages/PropertyHistory';
import Settings from './pages/Settings';
import Privacy from './pages/Privacy';
import TenantSignIn from './tenant/TenantSignIn';
import TenantInvoiceList from './tenant/TenantInvoiceList';
import TenantSetup from './tenant/TenantSetup';
import TenantInvoiceDetail from './tenant/TenantInvoiceDetail';

function FullPageSpinner() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
      <CircularProgress />
    </Box>
  );
}

// Owner routes: require auth + owner status. Redirects tenants to /tenant/sign-in.
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isOwner } = useAuth();
  if (loading) return <FullPageSpinner />;
  if (!user) return <Navigate to="/sign-in" replace />;
  if (isOwner === null) return <FullPageSpinner />;
  if (!isOwner) return <Navigate to="/tenant/sign-in" replace />;
  return <>{children}</>;
}

// Tenant routes: require auth + non-owner. Redirects owners to /.
function TenantProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isOwner } = useAuth();
  if (loading) return <FullPageSpinner />;
  if (!user) return <Navigate to="/tenant/sign-in" replace />;
  if (isOwner === null) return <FullPageSpinner />;
  if (isOwner) return <Navigate to="/" replace />;
  return <>{children}</>;
}

// Owner sign-in: redirect if already authenticated.
function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isOwner } = useAuth();
  if (loading) return <FullPageSpinner />;
  if (!user) return <>{children}</>;
  if (isOwner === null) return <FullPageSpinner />;
  if (isOwner) return <Navigate to="/" replace />;
  return <Navigate to="/onboard" replace />;
}

export default function App() {
  return (
    <AppThemeProvider>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route
            path="/sign-in"
            element={
              <PublicOnlyRoute>
                <SignIn />
              </PublicOnlyRoute>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/properties/:propertyId"
            element={
              <ProtectedRoute>
                <PropertyDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/properties/:propertyId/history"
            element={
              <ProtectedRoute>
                <PropertyHistory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />

          <Route path="/onboard" element={<Onboard />} />

          {/* Tenant routes */}
          <Route path="/tenant/sign-in" element={<TenantSignIn />} />
          <Route
            path="/tenant"
            element={
              <TenantProtectedRoute>
                <TenantInvoiceList />
              </TenantProtectedRoute>
            }
          />
          <Route
            path="/tenant/setup"
            element={
              <TenantProtectedRoute>
                <TenantSetup />
              </TenantProtectedRoute>
            }
          />
          <Route
            path="/tenant/invoices/:invoiceId"
            element={
              <TenantProtectedRoute>
                <TenantInvoiceDetail />
              </TenantProtectedRoute>
            }
          />

          {/* Public routes */}
          <Route path="/privacy" element={<Privacy />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </AppThemeProvider>
  );
}
